import React, { useState, useMemo } from 'react';
import { View, ScrollView, LogBox, Dimensions, Modal } from 'react-native';
import { interpolateBlues, schemeCategory10 } from 'd3-scale-chromatic';
import { scaleOrdinal } from 'd3-scale';
import LineChartScreen from '../common/charts/lineChart-builder';
import PaperTableDynamic from '../common/controls/TableProvider';
import PieChartComp from '../common/charts/piChart-builder';
import { getComplianceConfig } from '../common/service/dataOperations';
import { useAuth } from '../../../providers/AuthProvider';
import { useClient } from '../../../providers/ClientProvider';
import { Loader } from '../../Common/Loader';
import ToastMessage from '../common/controls/Toast';
import { FilterIcon, ExportIcon, FilterBox, SelectedFilters } from './ReUsableComps';
import {
  filterData,
  handleFilterReset,
  handleExportClick,
  constructData,
  EnableExport,
  CommonStyles,
  GetConsolidatedLog,
  FilterChoices
} from './ConsolidatedController';
import formCustomStyles from '../common/forms/formStyles';
import { TabScreen, Tabs, TabsProvider } from 'react-native-paper-tabs';
import { colorType5, logStyles } from '../LogControllers/LogStyles';
import LogKpiRow from '../LogControllers/LogKpiRow';
import LogDateRangeFilter from '../common/controls/LogDateRangeFilter';
import processData from '../common/service/dataProcessor';

interface Props {
  siteId: string;
  logTitle: string;
  projectId: string;
  isDashboard?: boolean;
  packageFilter?: string[] | null;
}

import { useQuery } from '@tanstack/react-query';

export default function ConsolVibrationMonitoring(props: Props) {
  LogBox.ignoreLogs(["EventEmitter.removeListener"]);

  const { getAccessToken } = useAuth();
  const { clientAPIURL } = useClient();

  const logQuery = useQuery({
    queryKey: ['consolidated-log', props.logTitle, props.projectId],
    queryFn: async () => {
      const accessToken = await getAccessToken();
      const jResponse = await GetConsolidatedLog(props.logTitle, clientAPIURL, accessToken, props.projectId);
      return constructData(jResponse);
    },
  });

  const complianceQuery = useQuery({
    queryKey: ['compliance-config', props.projectId],
    queryFn: async () => {
      const accessToken = await getAccessToken();
      return await getComplianceConfig(clientAPIURL, accessToken, props.projectId);
    },
  });

  if (logQuery.isLoading || complianceQuery.isLoading) return <Loader />;

  return logQuery.data && logQuery.data.items && logQuery.data.columns ? (
    <CreateVibrationLogCharts
      complianceConfig={complianceQuery.data}
      vibrationLogData={logQuery.data.items}
      vibrationLogColumns={logQuery.data.columns}
      siteId={props.siteId}
      logTitle={props.logTitle}
      isDashboard={props.isDashboard}
      packageFilter={props.packageFilter}
    />
  ) : null;
}

interface CreateVibrationLogChartsProps {
  complianceConfig: any;
  vibrationLogData: any;
  vibrationLogColumns: any;
  siteId: string;
  logTitle: string;
  isDashboard?: boolean;
  packageFilter?: string[] | null;
}

const CreateVibrationLogCharts: React.FC<CreateVibrationLogChartsProps> = ({ complianceConfig, vibrationLogData, vibrationLogColumns, siteId, logTitle, isDashboard, packageFilter }) => {
  const [FilterVisible, setFilterVisible] = React.useState(false);
  const screenWidth = Dimensions.get('window').width;

  const ColumnsWithIDColumn = vibrationLogColumns && [
    ...vibrationLogColumns,
    {
      id: 'CODE_GENERATED_ID',
      title: 'Item ID',
      columnName: 'ID',
      list: '7ae860e9-6c67-46d0-b65e-f2869542a3aa',
      datatype: 'calculated',
      createdAt: 'YOUR_CREATED_AT_TIMESTAMP',
      modifiedAt: 'YOUR_MODIFIED_AT_TIMESTAMP',
      required: false,
      subType: 'Float',
      choiceChoices: null,
      choiceType: null,
      choiceMultiselect: null,
      multiline: false,
      order: 0,
      calculatedFormula: null,
      listNavigation: null,
    }
  ];

  const updatedData = useMemo(() => {
    const raw = processData(logTitle, vibrationLogData, complianceConfig);
    if (!packageFilter?.length) return raw;
    return raw.filter((item: any) => packageFilter.includes(item.packageName));
  }, [logTitle, vibrationLogData, complianceConfig, packageFilter]);

  const [selectedYear, setSelectedYear] = useState<string>('ALL');
  const [selectedMonth, setSelectedMonth] = useState<string>('ALL');
  const [selectedPackage, setSelectedPackage] = useState<string>('ALL');
  const [toastVisibility, setToastVisibility] = useState<boolean>(false);
  const [toastText, setToastText] = useState<string>('');
  const [newData, setNewData] = useState<any[]>(updatedData);
  const [dateRangeFiltered, setDateRangeFiltered] = useState<any[]>(updatedData);

  const uniqueYearsArray = [...new Set(updatedData.map((item: any) => item.Year))];
  const yearFilterOptions = FilterChoices(uniqueYearsArray, "Year");
  const uniqueMonthsArray = [...new Set(updatedData.map((item: any) => item.Month))];
  const monthFilterOptions = FilterChoices(uniqueMonthsArray, "Month");
  const uniquePackagesArray = [...new Set(updatedData.map((item: any) => item.packageName))];
  const packageFilterOptions = FilterChoices(uniquePackagesArray, "Package");

  function handleYearFilterSelect(val: string) {
    setSelectedYear(val);
    filterData(val, selectedMonth, selectedPackage, setToastVisibility, updatedData, setToastText, setNewData);
  }
  function handleMonthFilterSelect(val: string) {
    setSelectedMonth(val);
    filterData(selectedYear, val, selectedPackage, setToastVisibility, updatedData, setToastText, setNewData);
  }
  function handlePackageFilterSelect(val: string) {
    filterData(selectedYear, selectedMonth, val, setToastVisibility, updatedData, setToastText, setNewData);
    setSelectedPackage(val);
  }
  function handleToastDismiss() {
    setToastVisibility(false);
  }

  const chartData = useMemo(() => {
    const uniquePeriod = [...new Set(dateRangeFiltered.map((item: any) => item.Period))] as string[];

    const colorLine = scaleOrdinal(schemeCategory10);
    const makeDatasets = (series: number[][]) =>
      series.map((values, index) => ({
        data: values,
        strokeWidth: 2,
        color: (opacity = 1) => colorLine(String(index)) + Math.round(opacity * 255).toString(16),
      }));

    // Conformity / Non Conformity counts per period
    const confPerPeriod = uniquePeriod.map(p => dateRangeFiltered.filter((i: any) => i.Period === p && i.Conformity === 'Conformity').length);
    const nonConfPerPeriod = uniquePeriod.map(p => dateRangeFiltered.filter((i: any) => i.Period === p && i.Conformity === 'Non Conformity').length);

    // LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=(opacity)=>string
    const lineData1 = { labels: uniquePeriod, datasets: makeDatasets([confPerPeriod, nonConfPerPeriod]) };

    // Sum and Average MonitoringResultsMaxPPVmms per period
    const sumByPeriod = uniquePeriod.map(p => {
      return dateRangeFiltered
        .filter((i: any) => i.Period === p)
        .reduce((acc: number, i: any) => acc + i.MonitoringResultsMaxPPVmms, 0);
    });
    const avgByPeriod = uniquePeriod.map(p => {
      const items = dateRangeFiltered.filter((i: any) => i.Period === p);
      if (items.length === 0) return 0;
      return items.reduce((acc: number, i: any) => acc + i.MonitoringResultsMaxPPVmms, 0) / items.length;
    });
    const lineDataLevelChart = { labels: uniquePeriod, datasets: makeDatasets([sumByPeriod, avgByPeriod]) };

    // PieChartComp contract: data[].name=string (non-null, non-empty), data[].value=number, data[].color=string
    const pieAcc: Record<string, number> = {};
    dateRangeFiltered.forEach((i: any) => { if (i.Conformity) pieAcc[i.Conformity] = (pieAcc[i.Conformity] ?? 0) + 1; });
    const byConformityPieData = Object.entries(pieAcc).map(([name, value], idx) => ({
      name, value, color: colorType5(idx.toString()),
    }));

    return { uniquePeriod, lineData1, lineDataLevelChart, byConformityPieData };
  }, [dateRangeFiltered]);

  const legendData1 = [{ name: 'Conformity' }, { name: 'Non Conformity' }];
  const legendData2 = [{ name: 'Maximum' }, { name: 'Average' }];

  const exceedanceDateSet = new Set(
    dateRangeFiltered.filter((i: any) => i.Conformity === 'Non Conformity' && i.Date).map((i: any) => i.Date)
  );
  const allDateSet = new Set(dateRangeFiltered.filter((i: any) => i.Date).map((i: any) => i.Date));
  const exceedanceDays = exceedanceDateSet.size;
  const nonExceedanceDays = [...allDateSet].filter(d => !exceedanceDateSet.has(d)).length;
  const exceedanceEntries = dateRangeFiltered.filter((i: any) => i.Conformity === 'Non Conformity').length;
  const nonExceedanceEntries = dateRangeFiltered.filter((i: any) => i.Conformity === 'Conformity').length;

  const kpiItems = [
    { label: 'Total', value: allDateSet.size, unit: 'days', subtitle: `${dateRangeFiltered.length} entries`, color: '#1565C0' },
    { label: 'Non Exceedances', value: nonExceedanceDays, unit: 'days', subtitle: `${nonExceedanceEntries} entries`, color: '#2E7D32' },
    { label: 'Exceedances', value: exceedanceDays, unit: 'days', subtitle: `${exceedanceEntries} entries`, color: '#C62828' },
  ];

  return (
    <View style={logStyles.logRoot}>
      <View style={CommonStyles.ribbonIconContainer}>
        <View style={CommonStyles.IconContainer}>
          <FilterIcon setFilterVisible={setFilterVisible} FilterVisible={FilterVisible} />
        </View>
        <View style={CommonStyles.IconContainer}>
          {EnableExport() === true
            ? <ExportIcon handleExportClick={handleExportClick} newData={newData} />
            : null
          }
        </View>
        <SelectedFilters selectedMonth={selectedMonth} selectedPackage={selectedPackage} selectedYear={selectedYear} />
      </View>
      <Modal
        animationType="fade"
        transparent={true}
        visible={FilterVisible}
        onRequestClose={() => { setFilterVisible(!FilterVisible); }}>
        <View style={formCustomStyles.centeredView}>
          <FilterBox
            FilterVisible={FilterVisible}
            setFilterVisible={setFilterVisible}
            screenWidth={screenWidth}
            CommonStyles={CommonStyles}
            yearFilterOptions={yearFilterOptions}
            handleYearFilterSelect={handleYearFilterSelect}
            selectedYear={selectedYear}
            monthFilterOptions={monthFilterOptions}
            handleMonthFilterSelect={handleMonthFilterSelect}
            selectedMonth={selectedMonth}
            packageFilterOptions={packageFilterOptions}
            handlePackageFilterSelect={handlePackageFilterSelect}
            selectedPackage={selectedPackage}
            handleFilterReset={handleFilterReset}
            setSelectedMonth={setSelectedMonth}
            setSelectedYear={setSelectedYear}
            setSelectedPackage={setSelectedPackage}
            setNewData={setNewData}
            updatedData={updatedData}
          />
        </View>
        <View>
          <ToastMessage
            show={toastVisibility}
            actionLabel="Dismiss"
            toastMessage={toastText}
            duration={3000}
            onDismiss={handleToastDismiss}
          />
        </View>
      </Modal>
      {isDashboard ? (
        <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
          <View style={{ zIndex: 200 }}>
            <LogKpiRow items={kpiItems}>
              <LogDateRangeFilter data={newData} onFilterChange={setDateRangeFiltered} />
            </LogKpiRow>
          </View>
          <View>
            <View style={CommonStyles.container}>
              {/* PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string */}
              <View style={[CommonStyles.card, { backgroundColor: '' }]}>
                <PieChartComp data={chartData.byConformityPieData} colorPallate={interpolateBlues} subTitle={'(Cumulative)'} ChartTitle={"Vibration Results"} />
              </View>
              <View style={[CommonStyles.card, { backgroundColor: '' }]}>
                <LineChartScreen legend={legendData1} data={chartData.lineData1} label={chartData.uniquePeriod} subTitle={'(Monthly by Conformity)'} ChartTitle={"Vibration Results"} />
              </View>
              <View style={[CommonStyles.card, { backgroundColor: '' }]}>
                <LineChartScreen legend={legendData2} data={chartData.lineDataLevelChart} label={chartData.uniquePeriod} subTitle={'(Monthly by Average)'} ChartTitle={"Total"} />
              </View>
            </View>
          </View>
        </ScrollView>
      ) : (
        <TabsProvider defaultIndex={0}>
          <Tabs style={{ backgroundColor: '#fff', height: "100%" }} showLeadingSpace={true}>
            <TabScreen label="Charts" icon="chart-bar">
              <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
                <View style={{ zIndex: 200 }}>
                  <LogKpiRow items={kpiItems}>
                    <LogDateRangeFilter data={newData} onFilterChange={setDateRangeFiltered} />
                  </LogKpiRow>
                </View>
                <View>
                  <View style={CommonStyles.container}>
                    {/* PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string */}
                    <View style={[CommonStyles.card, { backgroundColor: '' }]}>
                      <PieChartComp data={chartData.byConformityPieData} colorPallate={interpolateBlues} subTitle={'(Cumulative)'} ChartTitle={"Vibration Results"} />
                    </View>
                    <View style={[CommonStyles.card, { backgroundColor: '' }]}>
                      <LineChartScreen legend={legendData1} data={chartData.lineData1} label={chartData.uniquePeriod} subTitle={'(Monthly by Conformity)'} ChartTitle={"Vibration Results"} />
                    </View>
                    <View style={[CommonStyles.card, { backgroundColor: '' }]}>
                      <LineChartScreen legend={legendData2} data={chartData.lineDataLevelChart} label={chartData.uniquePeriod} subTitle={'(Monthly by Average)'} ChartTitle={"Total"} />
                    </View>
                  </View>
                </View>
              </ScrollView>
            </TabScreen>
            <TabScreen label="List" icon="database">
              <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
                <View>
                  <View style={CommonStyles.container}>
                    <View style={CommonStyles.cardThree}>
                      {dateRangeFiltered && dateRangeFiltered.length > 0 && ColumnsWithIDColumn && (
                        <PaperTableDynamic
                          data={dateRangeFiltered}
                          columns={ColumnsWithIDColumn}
                          siteId={siteId}
                          logTitle={logTitle}
                          updateData={() => {}}
                          isConsolidatedTable={true}
                        />
                      )}
                    </View>
                  </View>
                </View>
              </ScrollView>
            </TabScreen>
          </Tabs>
        </TabsProvider>
      )}
    </View>
  );
};
